import React, { useState, useEffect } from 'react';

import {
  Grid,
  LinearProgress,
  makeStyles,
  Typography,
} from '@material-ui/core';
import SkillSearchBox from './skillSearchBox';
import { IMG_EMPTY_MATCHING } from 'images';
import { AddCircleRounded } from '@material-ui/icons';
import { skillsRecommendation } from 'controllers/taxonomies';
import { useStore } from './context';

const useStyles = makeStyles(() => ({
  container: {
    padding: 32,
    background: '#fff',
    boxShadow: '0 1px 8px 0 rgba(0, 0, 0, 0.06)',
    minHeight: 600,
    height: 'calc(100vh - 165px)',
    overflow: 'hidden',
  },
  emptyImage: {
    width: '100%',
    height: '100%',
  },
  skillsListContainer: {
    paddingTop: 20,
    overflow: 'hidden',
    height: '100%',
  },
  skillsListTitle: {
    fontFamily: 'Roboto',
    fontSize: 14,
    fontWeight: 500,
    fontStyle: 'normal',
    letterSpacing: 0,
    textAlign: 'left',
    color: 'rgba(0, 0, 0, 0.6)',
    lineHeight: 1,
    paddingBottom: 16,
  },
  skillsListDivider: {
    margin: 0,
    opacity: 0.2,
  },
  skillsContainer: {
    overflow: 'hidden',
    height: '100%',
    padding: '10px 2px',
    display: 'flex',
    alignContent: 'center',
    flexDirection: 'column',
  },
  skillsList: {
    height: 'calc(100vh - 350px)',
    overflowX: 'auto',
  },
  skillBox: {
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    height: 58,
    display: 'flex',
    alignItems: 'center',
    margin: '5px 0',
    width: 'calc(100% - 48px)',
    padding: '0 24px',

    '&:hover': {
      background: 'rgba(0, 0, 0, 0.025)',
      cursor: 'pointer',
    },
  },
  skillContent: {
    width: '100%',
  },
  skill: {
    fontFamily: 'Roboto',
    fontSize: 14,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 1,
    letterSpacing: 0.25,
    color: '#60676f',
    paddingBottom: 10,
  },
  addIcon: {
    color: '#4b8c6f',
    paddingRight: 15,
  },
  progressBar: {
    borderRadius: 3,
    height: 6,
  },
}));

const SkillSelector = ({ label = '', skillSet = '' }) => {
  const classes = useStyles();
  const { state, dispatch } = useStore();
  const selectedSkillSet = state[skillSet];

  const [recommendedSkills, setRecommendedSkills] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const responseData = await skillsRecommendation(selectedSkillSet);
      const formattedSkills = Object.entries(responseData.response);

      setRecommendedSkills(formattedSkills);
    };

    if (selectedSkillSet.length) {
      fetchData();
    }

    if (!selectedSkillSet.length) {
      setRecommendedSkills([]);
    }
  }, [selectedSkillSet]);

  const onSelectSkill = async (skill) => {
    const uniqueSkills = [...new Set([...selectedSkillSet, skill])];

    dispatch({
      type: 'SET_SKILL_SET',
      skillSet,
      value: uniqueSkills,
    });
  };

  const onClickSkill = (skill) => () => {
    onSelectSkill(skill);
  };

  return (
    <>
      <Grid container classes={{ root: classes.container }}>
        <Grid item xs={12}>
          <SkillSearchBox
            id="skill-box"
            inputProps={{
              label,
            }}
            onSelectSkill={onSelectSkill}
          />
          {recommendedSkills.length ? (
            <Grid container classes={{ root: classes.skillsListContainer }}>
              <Grid item xs={12}>
                <Typography classes={{ root: classes.skillsListTitle }}>
                  Recommended skills
                </Typography>
                <hr className={classes.skillsListDivider} />
              </Grid>
              <Grid item xs={12} classes={{ root: classes.skillsContainer }}>
                <div className={classes.skillsList}>
                  {recommendedSkills.map(([skill, score]) => (
                    <div
                      className={classes.skillBox}
                      onClick={onClickSkill(skill)}
                    >
                      <AddCircleRounded classes={{ root: classes.addIcon }} />
                      <div className={classes.skillContent}>
                        <Typography classes={{ root: classes.skill }}>
                          {skill}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={score * 10}
                          classes={{ root: classes.progressBar }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          ) : (
            <img
              src={IMG_EMPTY_MATCHING}
              alt="Empty skill list"
              className={classes.emptyImage}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default SkillSelector;
